import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, RotateCcw, Download, RefreshCw, Building2, CalendarClock, Hash, ChevronDown } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useCurrentDomain } from '../../context/DomainContext';
import { getAICheckPostingReport } from '../../../src/services/masterDataService';
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AICheckPostingReport.css';
import { predefinedBottomRanges, selectStyles, useColumnResizePct } from '../../helpers';
import { THEME } from '../../theme/themeColors';
import { useThemeHex } from '../../theme/useThemeHex';

const TREND_WEEKS_BACK = 5;

const COLUMNS = [
    { label: 'Posting Source', key: 'CheckPostingSource' },
    { label: 'Comp Name',      key: 'CompanyName' },
    { label: 'Payment Id',     key: 'PaymentId' },
    { label: 'Bill Id',        key: 'BillId' },
    { label: 'Payment Date',   key: 'PaymentDate' },
    { label: 'Check Amount',   key: 'CheckAmount' },
    { label: 'Created By',     key: 'CreatedUserName' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }));

const formatCurrency = (val) => {
    if (val === null || val === undefined || val === '') return '—';
    const num = Number(val);
    if (Number.isNaN(num)) return '—';
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const COLUMN_DIVIDER = '1px solid #e5e7eb';
const MIN_COL_PX = [120, 160, 130, 100, 150, 110, 130];
const MIN_TABLE_PX = MIN_COL_PX.reduce((a, b) => a + b, 0);
const DEFAULT_COL_WIDTHS_PCT = [11.2, 20.6, 17.8, 9.4, 16.8, 11.2, 13.0];

const CHART_MOBILE_BREAKPOINT = 950;

const useIsMobile = (breakpoint = 580) => {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
};

const thStyle = (widthPct, isLast, minPx) => ({
    width: `${widthPct}%`,
    minWidth: minPx ? `${minPx}px` : undefined,
    fontSize: '10.5px',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRight: isLast ? 'none' : COLUMN_DIVIDER,
});

const tdStyle = (widthPct, isLast, minPx) => ({
    width: `${widthPct}%`,
    minWidth: minPx ? `${minPx}px` : undefined,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    verticalAlign: 'top',
    borderRight: isLast ? 'none' : COLUMN_DIVIDER,
});

const AICheckPostingReport = () => {
    const { domain } = useCurrentDomain();
    const isMobile = useIsMobile(580);
    const [isChartMobile, setIsChartMobile] = useState(false);
    // Recharts needs a real hex (no CSS vars in SVG fill)
    const chartPrimary = useThemeHex('--theme-primary');

    const [records, setRecords]                 = useState([]);
    const [loading, setLoading]           = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [totalCount, setTotalCount]     = useState(0);
    const [pageNumber, setPageNumber]     = useState(1);
    const [pageSize, setPageSize]         = useState(50);
    const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 35); return d; });
    const [toDate, setToDate]     = useState(new Date());
    const [isResetHovered, setIsResetHovered] = useState(false);
    const [isExcelHovered, setIsExcelHovered] = useState(false);

    const [colWidths, setColWidths] = useState(DEFAULT_COL_WIDTHS_PCT);
    const tableRef = useRef(null);

    const isFirstDateEffect = useRef(true);

    const { handleMouseDown } = useColumnResizePct(colWidths, setColWidths, tableRef);

    const [viewMode, setViewMode] = useState('summary');
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [weekBuckets, setWeekBuckets] = useState([]);
    const [pivotRows, setPivotRows] = useState([]);
    const [expandedCompanies, setExpandedCompanies] = useState(new Set());

    const [trendLoading, setTrendLoading] = useState(false);
    const [trendData, setTrendData] = useState([]);

    useEffect(() => {
        const checkChartMobile = () => setIsChartMobile(window.innerWidth < CHART_MOBILE_BREAKPOINT);
        checkChartMobile();
        window.addEventListener('resize', checkChartMobile);
        return () => window.removeEventListener('resize', checkChartMobile);
    }, []);

    const fetchRecords = useCallback(async (page, size, isNewSearch) => {
        const setLoader = isNewSearch ? setSearchLoading : setLoading;
        setLoader(true);
        try {
            const payload = {
                domain: domain,
                FromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                ToDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
                PageNumber: page,
                PageSize: size,
                IsExport: false,
            }
            const res = await getAICheckPostingReport(payload);
            setRecords(res.details || []);
            setTotalCount(res.totalCount || 0);
            setPageNumber(res.pageNumber || page);
            setPageSize(res.pageSize || size);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load report data');
            console.error(err);
        } finally {
            setLoader(false);
        }
    }, [domain, fromDate, toDate]);

    const buildWeeklyBuckets = (rangeFrom, rangeTo) => {
        if (!rangeFrom || !rangeTo) return [];
        const today = moment().endOf('day');
        const effectiveTo = moment(rangeTo).isAfter(today) ? today : moment(rangeTo);
        if (moment(rangeFrom).startOf('day').isAfter(effectiveTo)) return [];

        const weeks = [];
        let cursor = moment(effectiveTo).startOf('week');
        const firstWeekStart = moment(rangeFrom).startOf('week');
        const clampFrom = moment(rangeFrom).startOf('day');
        let safety = 0;
        while (cursor.isSameOrAfter(firstWeekStart) && safety < 3000) {
            const start = cursor.clone();
            const end = cursor.clone().endOf('week');

            const displayStart = start.isBefore(clampFrom) ? clampFrom.clone() : start;
            const displayEnd = end.isAfter(effectiveTo) ? effectiveTo.clone() : end;
            weeks.push({
                start,
                end,
                key: start.format('YYYY-MM-DD'),
                label: `${displayEnd.format('MM/DD/YY')} - ${displayStart.format('MM/DD/YY')}`,
                shortLabel: start.format('MM/DD'),
            });
            cursor = cursor.clone().subtract(7, 'days');
            safety += 1;
        }
        return weeks;
    };

    const buildPivotData = (allRecords, weeks) => {
        const companyMap = new Map();
        (allRecords || []).forEach((row) => {
            const company = row.companyName || 'Unknown';
            const isAI = (row.checkPostingSource || '').toUpperCase() === 'AI MODULE';

            if (!companyMap.has(company)) {
                const weekly = {};
                weeks.forEach((w) => { weekly[w.label] = { ai: 0, manual: 0, aiAmount: 0, manualAmount: 0 }; });
                companyMap.set(company, {
                    companyName: company,
                    total: { ai: 0, manual: 0, aiAmount: 0, manualAmount: 0 },
                    weekly,
                });
            }
            const entry = companyMap.get(company);
            const amount = Number(row.checkAmount) || 0;
            if (isAI) {
                entry.total.ai += 1;
                entry.total.aiAmount += amount;
            } else {
                entry.total.manual += 1;
                entry.total.manualAmount += amount;
            }

            const paymentMoment = row.paymentDate ? moment(row.paymentDate) : null;
            if (paymentMoment) {
                const week = weeks.find((w) => paymentMoment.isBetween(w.start, w.end, null, '[]'));
                if (week) {
                    if (isAI) {
                        entry.weekly[week.label].ai += 1;
                        entry.weekly[week.label].aiAmount += amount;
                    } else {
                        entry.weekly[week.label].manual += 1;
                        entry.weekly[week.label].manualAmount += amount;
                    }
                }
            }
        });
        return Array.from(companyMap.values()).sort((a, b) => a.companyName.localeCompare(b.companyName));
    };

    const fetchSummaryData = useCallback(async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        setSummaryLoading(true);
        try {
            const payload = {
                domain: domain,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
                IsExport: true,
            };
            const res = await getAICheckPostingReport(payload);
            const allRecords = res.details || [];
            const weeks = buildWeeklyBuckets(fromDate, toDate);
            setWeekBuckets(weeks);
            setPivotRows(buildPivotData(allRecords, weeks));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load summary data');
            console.error(err);
        } finally {
            setSummaryLoading(false);
        }
    }, [domain, fromDate, toDate]);

    const buildTrendWeeklyBuckets = (rangeFrom, rangeTo, maxWeeks = TREND_WEEKS_BACK) => {
        const allWeeks = buildWeeklyBuckets(rangeFrom, rangeTo);
        return allWeeks.slice(0, maxWeeks);
    };

    const buildTrendWeeklyData = (allRecords, weeks) => {
        const byKey = new Map(weeks.map((w) => [w.key, { label: w.label, shortLabel: w.shortLabel, ai: 0, manual: 0 }]));
        (allRecords || []).forEach((row) => {
            if (!row.paymentDate) return;
            const paymentMoment = moment(row.paymentDate);
            const week = weeks.find((w) => paymentMoment.isBetween(w.start, w.end, null, '[]'));
            if (!week) return;
            const bucket = byKey.get(week.key);
            const isAI = (row.checkPostingSource || '').toUpperCase() === 'AI MODULE';
            if (isAI) bucket.ai += 1; else bucket.manual += 1;
        });
        return weeks.map((w) => byKey.get(w.key));
    };

const fetchWeeklyTrend = useCallback(async () => {
    if (!fromDate || !toDate) {
        setTrendData([]);
        return;
    }
    setTrendLoading(true);
    try {
        const weeks = buildTrendWeeklyBuckets(fromDate, toDate);
        if (weeks.length === 0) {
            setTrendData([]);
            return;
        }
        const earliestWeek = weeks[weeks.length - 1];
        const selectedFrom = moment(fromDate).startOf('day');
        const effectiveFrom = moment.max(earliestWeek.start, selectedFrom);

        const payload = {
            domain: domain,
            FromDate: effectiveFrom.format('YYYY-MM-DD'),
            ToDate: moment(toDate).format('YYYY-MM-DD'),
            IsExport: true,
        };
        const res = await getAICheckPostingReport(payload);
        setTrendData(buildTrendWeeklyData(res.details || [], weeks));
    } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load trend data');
        console.error(err);
    } finally {
        setTrendLoading(false);
    }
}, [domain, fromDate, toDate]);

    useEffect(() => {
        if (!domain) return;
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    }, [domain]);

    useEffect(() => {
        if (!domain) return;
        fetchWeeklyTrend();
    }, [domain, fetchWeeklyTrend]);

    useEffect(() => {
        if (isFirstDateEffect.current) {
            isFirstDateEffect.current = false;
            return;
        }
        if (!fromDate || !toDate) return;
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    }, [fromDate, toDate]);

    const handleSearch = () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (viewMode === 'summary') fetchSummaryData();
        else fetchRecords(1, pageSize, true);
    };

    const handleViewModeChange = (mode) => {
        if (mode === viewMode) return;
        setViewMode(mode);
        if (!fromDate || !toDate) return;
        if (mode === 'summary') fetchSummaryData();
        else fetchRecords(pageNumber, pageSize, false);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    const goToPage = (page) => {
        if (page < 1 || page > totalPages || loading) return;
        fetchRecords(page, pageSize, false);
    };

    const handlePageSizeChange = (opt) => {
        fetchRecords(1, opt.value, false);
    };

    const grandTotal = pivotRows.reduce((acc, row) => {
        acc.total.ai += row.total.ai;
        acc.total.manual += row.total.manual;
        acc.total.aiAmount += row.total.aiAmount;
        acc.total.manualAmount += row.total.manualAmount;
        weekBuckets.forEach((w) => {
            if (!acc.weekly[w.label]) acc.weekly[w.label] = { ai: 0, manual: 0 };
            acc.weekly[w.label].ai += row.weekly[w.label]?.ai || 0;
            acc.weekly[w.label].manual += row.weekly[w.label]?.manual || 0;
        });
        return acc;
    }, { total: { ai: 0, manual: 0, aiAmount: 0, manualAmount: 0 }, weekly: {} });

    const exportDetailedToExcel = async () => {
        const tid = toast.loading('Preparing export...');
        try {
            const payload = {
                domain: domain,
                FromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                ToDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
                IsExport: true,
            }

            const data = await getAICheckPostingReport(payload);

            if (!data?.details?.length) { toast.error('No data to export'); return; }

            const exportData = data?.details?.map(row => ({
                POSTING_SOURCE:  row.checkPostingSource || '',
                COMPANY_NAME:    row.companyName || '',
                PAYMENT_ID:      row.paymentId || '',
                BILL_ID:         row.billId || '',
                PAYMENT_DATE:    row.paymentDate ? new Date(row.paymentDate).toLocaleString() : '',
                CHECK_AMOUNT:    row.checkAmount ?? '',
                CREATED_BY:      row.createdUserName || '',
            }));
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'AICheckPosting');
            XLSX.writeFile(wb, `AICheckPostingReport_Detailed_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? 's' : ''}`);
        } catch {
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportSummaryToExcel = () => {
        if (!pivotRows.length) { toast.error('No data to export'); return; }
        const tid = toast.loading('Preparing export...');
        try {
            const numWeekCols = weekBuckets.length * 2;
            const totalCols = 4 + numWeekCols;

            const headerRow1 = new Array(totalCols).fill('');
            headerRow1[0] = 'Sr.no';
            headerRow1[1] = 'Company Name';
            headerRow1[2] = 'Total';
            weekBuckets.forEach((w, i) => { headerRow1[4 + i * 2] = w.label; });

            const headerRow2 = new Array(totalCols).fill('');
            headerRow2[2] = 'AI Module';
            headerRow2[3] = 'Manual';
            weekBuckets.forEach((w, i) => {
                headerRow2[4 + i * 2] = 'AI Module';
                headerRow2[5 + i * 2] = 'Manual';
            });

            const dataRows = pivotRows.map((row, idx) => {
                const r = [idx + 1, row.companyName, row.total.ai, row.total.manual];
                weekBuckets.forEach((w) => {
                    r.push(row.weekly[w.label]?.ai || 0);
                    r.push(row.weekly[w.label]?.manual || 0);
                });
                return r;
            });

            const grandTotalRow = ['Grand Total', '', grandTotal.total.ai, grandTotal.total.manual];
            weekBuckets.forEach((w) => {
                grandTotalRow.push(grandTotal.weekly[w.label]?.ai || 0);
                grandTotalRow.push(grandTotal.weekly[w.label]?.manual || 0);
            });

            const aoa = [headerRow1, headerRow2, ...dataRows, grandTotalRow];
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            const lastRowIdx = aoa.length - 1;
            const merges = [
                { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
                { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
                { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } },
                { s: { r: lastRowIdx, c: 0 }, e: { r: lastRowIdx, c: 1 } },
            ];
            weekBuckets.forEach((_, i) => {
                merges.push({ s: { r: 0, c: 4 + i * 2 }, e: { r: 0, c: 5 + i * 2 } });
            });
            ws['!merges'] = merges;

            ws['!cols'] = [
                { wch: 6 }, { wch: 28 }, { wch: 10 }, { wch: 10 },
                ...weekBuckets.flatMap(() => [{ wch: 11 }, { wch: 11 }]),
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'WeeklySummary');
            XLSX.writeFile(wb, `AICheckPostingReport_WeeklySummary_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${pivotRows.length} compan${pivotRows.length !== 1 ? 'ies' : 'y'}`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportToExcel = async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (viewMode === 'summary') {
            exportSummaryToExcel();
        } else {
            await exportDetailedToExcel();
        }
    };

    const getSourceBadgeStyles = (src) => {
        switch ((src || '').toUpperCase()) {
            case 'AI MODULE': return { backgroundColor: THEME.alpha(0.12), color: THEME.primaryHover };
            case 'MANUAL':    return { backgroundColor: '#f1f5f9', color: '#475569' };
            default:          return { backgroundColor: '#f1f5f9', color: '#475569' };
        }
    };

    const toggleCompanyExpand = (companyName) => {
        setExpandedCompanies((prev) => {
            const next = new Set(prev);
            if (next.has(companyName)) next.delete(companyName);
            else next.add(companyName);
            return next;
        });
    };

    const filterLabelStyle = { fontSize: '10px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px', display: 'block' };

    const rangeStart = totalCount === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
    const rangeEnd   = Math.min(pageNumber * pageSize, totalCount);

    const renderCount = (value) => (
        value === 0 ? <span className="acp-zero-value">0</span> : value
    );

    return (
        <div className="bg-light min-vh-100 acp-page-wrap">
            <Toaster position="top-right" />

            <div className="card shadow-sm border-0 rounded-3 bg-white acp-card">

                <div className="border-bottom px-2 py-2" style={{ backgroundColor: '#fcfcfc' }}>
                    <div className="d-flex gap-2 align-items-end justify-content-between acp-toolbar-row">

                        <div className="acp-date-filter">
                            <label style={filterLabelStyle}>Payment Date</label>
                            <DateRangePicker
                                value={[fromDate || null, toDate || null]}
                                onChange={(value) => {
                                    if (!value || value.length !== 2) {
                                        setFromDate(null);
                                        setToDate(null);
                                        return;
                                    }

                                    setFromDate(value[0]);
                                    setToDate(value[1]);
                                }}
                                ranges={predefinedBottomRanges}
                                placeholder="MM/DD/YYYY ~ MM/DD/YYYY"
                                style={{ width: '100%' }}
                                format="MM/dd/yyyy"
                                size="sm"
                                showOneCalendar={isMobile}
                                placement="bottomStart"
                                preventOverflow
                                cleanable
                                menuClassName="acp-daterange-menu"
                            />
                        </div>

                        <div className="d-flex gap-2 acp-toolbar-actions">
                            <div className="btn-group acp-view-toggle" role="group" style={{ height: '32px' }}>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        fontSize: '12px', height: '32px',
                                        backgroundColor: viewMode === 'summary' ? THEME.primary : '#fff',
                                        color: viewMode === 'summary' ? THEME.onPrimary : '#333',
                                        border: '1px solid #dee2e6',
                                    }}
                                    onClick={() => handleViewModeChange('summary')}
                                    title="Weekly summary view"
                                >Weekly Summary</button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        fontSize: '12px', height: '32px',
                                        backgroundColor: viewMode === 'detailed' ? THEME.primary : '#fff',
                                        color: viewMode === 'detailed' ? THEME.onPrimary : '#333',
                                        border: '1px solid #dee2e6', borderLeft: 'none',
                                    }}
                                    onClick={() => handleViewModeChange('detailed')}
                                    title="Detailed view"
                                >Detailed View</button>
                            </div>
                            <button
                                className="btn fw-medium d-flex align-items-center justify-content-center gap-1 px-3 acp-toolbar-btn"
                                style={{ backgroundColor: THEME.primary, color: THEME.onPrimary, height: '32px', fontSize: '12px' }}
                                onClick={handleSearch} disabled={loading || searchLoading} title="Search"
                            >
                                {searchLoading
                                    ? <RefreshCw size={13} style={{ animation: 'acp-spin 1s linear infinite', color: THEME.onPrimary }} />
                                    : <Search size={13} style={{ color: THEME.onPrimary }} />}
                                <span className="acp-btn-label">Search</span>
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 px-3 acp-toolbar-btn"
                                style={{ height: '32px', fontSize: '12px', backgroundColor: isExcelHovered ? '#f8f9fa' : '#fff', color: '#333' }}
                                onMouseEnter={() => setIsExcelHovered(true)}
                                onMouseLeave={() => setIsExcelHovered(false)}
                                onClick={exportToExcel} title="Export to Excel"
                            >
                                <Download size={13} style={{ color: '#6c757d' }} />
                                <span className="acp-btn-label">Export</span>
                            </button>
                        </div>

                    </div>
                </div>

                {viewMode === 'detailed' && (
                <div className="table-responsive acp-desktop-table" style={{ maxHeight: '65vh', overflowY: 'auto' }} ref={tableRef}>
                    <table
                        className="table table-hover align-middle mb-0"
                        style={{ tableLayout: 'fixed', fontSize: '12.5px', width: '100%', minWidth: `${MIN_TABLE_PX}px` }}
                    >
                        <thead className="table-light sticky-top border-bottom" style={{ zIndex: 10 }}>
                            <tr>
                                {COLUMNS.map((col, index) => (
                                    <th
                                        key={index}
                                        className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                        style={thStyle(colWidths[index], index === COLUMNS.length - 1, MIN_COL_PX[index])}
                                    >
                                        <span className="d-flex align-items-center gap-1">
                                            {col.label}
                                            {index === 4 && (
                                                <span style={{ fontSize: '9px', color: THEME.primaryHover, fontWeight: 700 }}>↓</span>
                                            )}
                                        </span>
                                        <div
                                            onMouseDown={(e) => handleMouseDown(index, e)}
                                            style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                            className="acp-column-resizer-handle"
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {records.length === 0 && !loading && !searchLoading ? (
                                <tr>
                                    <td colSpan={COLUMNS.length} className="text-center py-4 text-muted border-0">
                                        No matching records found.
                                    </td>
                                </tr>
                            ) : (
                                records.map((row, idx) => {
                                    const badgeStyles = getSourceBadgeStyles(row.checkPostingSource);
                                    return (
                                        <tr key={`${row.paymentId || idx}-${row.billId || idx}`}>

                                            {/* 0 — Posting Source */}
                                            <td className="py-1 px-2" style={tdStyle(colWidths[0], false, MIN_COL_PX[0])} title={row.checkPostingSource || ''}>
                                                {row.checkPostingSource ? (
                                                    <span className="badge fw-bold acp-px-15 acp-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                        {row.checkPostingSource}
                                                    </span>
                                                ) : '—'}
                                            </td>

                                            {/* 1 — Company Name */}
                                            <td className="py-1 px-2 text-dark fw-medium" title={(row.companyName || '').toUpperCase()} style={{ ...tdStyle(colWidths[1], false, MIN_COL_PX[1]), textTransform: 'uppercase', fontSize: '11.5px' }}>
                                                {row.companyName || '—'}
                                            </td>

                                            {/* 2 — Payment Id */}
                                            <td className="py-1 px-2" title={row.paymentId || ''} style={tdStyle(colWidths[2], false, MIN_COL_PX[2])}>
                                                {row.paymentId || '—'}
                                            </td>

                                            {/* 3 — Bill Id */}
                                            <td className="py-1 px-2" title={row.billId || ''} style={tdStyle(colWidths[3], false, MIN_COL_PX[3])}>
                                                {row.billId || '—'}
                                            </td>

                                            {/* 4 — Payment Date */}
                                            <td className="text-secondary py-1 px-2" title={row.paymentDate ? new Date(row.paymentDate).toLocaleString() : ''} style={tdStyle(colWidths[4], false, MIN_COL_PX[4])}>
                                                {row.paymentDate ? new Date(row.paymentDate).toLocaleString() : '—'}
                                            </td>

                                            {/* 5 — Check Amount */}
                                            <td className="py-1 px-2" title={formatCurrency(row.checkAmount)} style={tdStyle(colWidths[5], false, MIN_COL_PX[5])}>
                                                {formatCurrency(row.checkAmount)}
                                            </td>

                                            {/* 6 — Created By */}
                                            <td className="py-1 px-2" title={row.createdUserName || ''} style={tdStyle(colWidths[6], true, MIN_COL_PX[6])}>
                                                {row.createdUserName || '—'}
                                            </td>

                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                )}

                {viewMode === 'detailed' && (
                <div className="acp-mobile-cards">
                    {records.length === 0 && !loading && !searchLoading ? (
                        <div className="text-center py-4 text-muted">No matching records found.</div>
                    ) : (
                        records.map((row, idx) => {
                            const paymentDateStr = row.paymentDate ? new Date(row.paymentDate).toLocaleString() : '—';
                            const badgeStyles = getSourceBadgeStyles(row.checkPostingSource);
                            const isAI = (row.checkPostingSource || '').toUpperCase() === 'AI MODULE';
                            return (
                                <div
                                    key={`card-${row.paymentId || idx}-${row.billId || idx}`}
                                    className={`acp-ecard ${isAI ? 'acp-ecard-ai' : 'acp-ecard-manual'}`}
                                >
                                    <div className="acp-ecard-top">
                                        <div className="acp-ecard-identity">
                                            <div className="acp-ecard-identity-text">
                                                <div className="acp-ecard-company" title={(row.companyName || '').toUpperCase()}>
                                                    <Building2 size={11} className="acp-ecard-inline-icon" />
                                                    <span style={{ textTransform: 'uppercase' }}>{row.companyName || '—'}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {row.checkPostingSource && (
                                            <span
                                                className="acp-ecard-badge"
                                                style={{ backgroundColor: badgeStyles.backgroundColor, color: badgeStyles.color }}
                                            >
                                                {row.checkPostingSource}
                                            </span>
                                        )}
                                    </div>

                                    <div className="acp-ecard-caseno">
                                        <Hash size={11} className="acp-ecard-inline-icon" />
                                        <span>Payment Id - {row.paymentId || '—'} · Bill Id - {row.billId || '—'}</span>
                                    </div>

                                    <div className="acp-ecard-divider" />

                                    <div className="acp-ecard-meta-grid">
                                        <div className="acp-ecard-meta-item">
                                            <span className="acp-ecard-meta-label">
                                                <CalendarClock size={11} className="acp-ecard-inline-icon" />
                                                Payment Date
                                            </span>
                                            <span className="acp-ecard-meta-value" title={paymentDateStr}>{paymentDateStr}</span>
                                            <span className="acp-ecard-meta-sub" title={row.createdUserName || ''}>
                                                by {row.createdUserName || '—'}
                                            </span>
                                        </div>
                                        <div className="acp-ecard-meta-item">
                                            <span className="acp-ecard-meta-label">Check Amount</span>
                                            <span className="acp-ecard-meta-value">{formatCurrency(row.checkAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                )}

                {viewMode === 'summary' && (
                    <div className="table-responsive acp-summary-table-wrap acp-summary-desktop-table" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                        {summaryLoading ? (
                            <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                                <RefreshCw size={14} style={{ animation: 'acp-spin 1s linear infinite' }} />
                                Loading summary...
                            </div>
                        ) : pivotRows.length === 0 ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            <table className="table table-bordered align-middle mb-0 acp-summary-table">
                                <thead className="sticky-top" style={{ zIndex: 10 }}>
                                    <tr>
                                        <th rowSpan={2} className="text-center align-middle acp-col-srno">Sr.no</th>
                                        <th rowSpan={2} className="align-middle acp-col-company">Company Name</th>
                                        <th colSpan={2} className="text-center acp-total-header">Total</th>
                                        {weekBuckets.map((w, i) => (
                                            <th key={w.label} colSpan={2} className={`text-center acp-week-header ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>{w.label}</th>
                                        ))}
                                    </tr>
                                    <tr>
                                        <th className="text-center acp-total-col-header acp-col-ai">AI Module</th>
                                        <th className="text-center acp-col-manual">Manual</th>
                                        {weekBuckets.map((w, i) => (
                                            <React.Fragment key={`${w.label}-sub`}>
                                                <th className={`text-center acp-week-divider acp-col-ai ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>AI Module</th>
                                                <th className={`text-center acp-col-manual ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>Manual</th>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pivotRows.map((row, idx) => (
                                        <tr key={row.companyName}>
                                            <td className="text-center acp-col-srno">{idx + 1}</td>
                                            <td className="text-dark fw-medium acp-col-company" title={(row.companyName || '').toUpperCase()} style={{ textTransform: 'uppercase', fontSize: '11px' }}>{row.companyName}</td>
                                            <td className="text-center acp-total-col acp-col-ai">{renderCount(row.total.ai)}</td>
                                            <td className="text-center acp-total-col acp-col-manual">{renderCount(row.total.manual)}</td>
                                            {weekBuckets.map((w, i) => (
                                                <React.Fragment key={`${row.companyName}-${w.label}`}>
                                                    <td className={`text-center acp-week-divider acp-col-ai ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>{renderCount(row.weekly[w.label].ai)}</td>
                                                    <td className={`text-center acp-col-manual ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>{renderCount(row.weekly[w.label].manual)}</td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="acp-grand-total-row">
                                        <td colSpan={2} className="acp-col-company acp-grand-total-label">Grand Total</td>
                                        <td className="text-center acp-total-col acp-col-ai">{grandTotal.total.ai}</td>
                                        <td className="text-center acp-total-col acp-col-manual">{grandTotal.total.manual}</td>
                                        {weekBuckets.map((w, i) => (
                                            <React.Fragment key={`gt-${w.label}`}>
                                                <td className={`text-center acp-week-divider acp-col-ai ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>{grandTotal.weekly[w.label]?.ai || 0}</td>
                                                <td className={`text-center acp-col-manual ${i % 2 === 0 ? 'acp-week-even' : 'acp-week-odd'}`}>{grandTotal.weekly[w.label]?.manual || 0}</td>
                                            </React.Fragment>
                                        ))}
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </div>
                )}

                {viewMode === 'summary' && (
                    <div className="acp-summary-mobile-cards">
                        {summaryLoading ? (
                            <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                                <RefreshCw size={14} style={{ animation: 'acp-spin 1s linear infinite' }} />
                                Loading summary...
                            </div>
                        ) : pivotRows.length === 0 ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            <>
                                <div className="acp-scard acp-scard-total">
                                    <div className="acp-scard-top">
                                        <div className="acp-scard-title">
                                            <span className="acp-scard-total-label">Grand Total</span>
                                            <span className="acp-scard-total-sub">{pivotRows.length} compan{pivotRows.length !== 1 ? 'ies' : 'y'}</span>
                                        </div>
                                    </div>
                                    <div className="acp-scard-stats">
                                        <div className="acp-scard-stat acp-scard-stat-ai">
                                            <span className="acp-scard-stat-value">{grandTotal.total.ai}</span>
                                            <span className="acp-scard-stat-label">AI Module</span>
                                        </div>
                                        <div className="acp-scard-stat acp-scard-stat-manual">
                                            <span className="acp-scard-stat-value">{grandTotal.total.manual}</span>
                                            <span className="acp-scard-stat-label">Manual</span>
                                        </div>
                                    </div>
                                </div>

                                {pivotRows.map((row, idx) => {
                                    const isExpanded = expandedCompanies.has(row.companyName);
                                    return (
                                        <div key={row.companyName} className="acp-scard">
                                            <button
                                                type="button"
                                                className="acp-scard-top acp-scard-toggle"
                                                onClick={() => toggleCompanyExpand(row.companyName)}
                                                aria-expanded={isExpanded}
                                            >
                                                <div className="acp-scard-title">
                                                    <span className="acp-scard-srno">{idx + 1}</span>
                                                    <span className="acp-scard-company" title={(row.companyName || '').toUpperCase()} style={{ textTransform: 'uppercase' }}>{row.companyName}</span>
                                                </div>
                                                <ChevronDown
                                                    size={16}
                                                    className={`acp-scard-chevron ${isExpanded ? 'acp-scard-chevron-open' : ''}`}
                                                />
                                            </button>

                                            <div className="acp-scard-stats">
                                                <div className="acp-scard-stat acp-scard-stat-ai">
                                                    <span className="acp-scard-stat-value">{renderCount(row.total.ai)}</span>
                                                    <span className="acp-scard-stat-label">AI Module</span>
                                                </div>
                                                <div className="acp-scard-stat acp-scard-stat-manual">
                                                    <span className="acp-scard-stat-value">{renderCount(row.total.manual)}</span>
                                                    <span className="acp-scard-stat-label">Manual</span>
                                                </div>
                                            </div>

                                            {isExpanded && (
                                                <div className="acp-scard-weeks">
                                                    {weekBuckets.map((w) => (
                                                        <div key={w.label} className="acp-scard-week-row">
                                                            <span className="acp-scard-week-label">{w.label}</span>
                                                            <span className="acp-scard-week-counts">
                                                                <span className="acp-scard-week-ai">{renderCount(row.weekly[w.label]?.ai || 0)} AI</span>
                                                                <span className="acp-scard-week-sep">·</span>
                                                                <span className="acp-scard-week-manual">{renderCount(row.weekly[w.label]?.manual || 0)} Manual</span>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}

                {viewMode === 'detailed' && !loading && !searchLoading && records.length > 0 && (
                    <div
                        className="acp-pagination-bar"
                        style={{
                            padding: '10px 20px', borderTop: '1px solid #e5e7eb',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: '#fafafa', flexWrap: 'wrap', gap: '8px',
                        }}
                    >
                        <span className="acp-pagination-summary" style={{ fontSize: 12, color: '#6b7280' }}>
                            {totalCount === 0
                                ? 'No records'
                                : <>Showing {rangeStart}–{rangeEnd} of {totalCount}</>}
                        </span>

                        <div className="acp-pagination-controls" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                            <button
                                onClick={() => goToPage(pageNumber - 1)}
                                disabled={pageNumber === 1 || loading}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                    background: pageNumber === 1 ? '#f3f4f6' : '#fff',
                                    color: pageNumber === 1 ? '#9ca3af' : THEME.primaryHover,
                                    cursor: pageNumber === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >‹ Prev</button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => {
                                    if (totalPages <= 7) return true;
                                    if (p === 1 || p === totalPages) return true;
                                    if (p >= pageNumber - 2 && p <= pageNumber + 2) return true;
                                    return false;
                                })
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === '...' ? (
                                        <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                                    ) : (
                                        <button
                                            key={p}
                                            onClick={() => goToPage(p)}
                                            disabled={loading}
                                            style={{
                                                width: 28, height: 28, borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                                background: pageNumber === p ? THEME.primary : '#fff',
                                                color: pageNumber === p ? THEME.onPrimary : THEME.primaryHover,
                                                fontWeight: pageNumber === p ? 700 : 400,
                                                cursor: 'pointer', fontSize: 12
                                            }}
                                        >
                                            {loading && p === pageNumber
                                                ? <RefreshCw size={12} style={{ animation: 'acp-spin 1s linear infinite' }} />
                                                : p}
                                        </button>
                                    )
                                )
                            }

                            <button
                                onClick={() => goToPage(pageNumber + 1)}
                                disabled={pageNumber === totalPages || loading}
                                style={{
                                    padding: '4px 8px', borderRadius: 6, border: `1px solid ${THEME.alpha(0.35)}`,
                                    background: pageNumber === totalPages ? '#f3f4f6' : '#fff',
                                    color: pageNumber === totalPages ? '#9ca3af' : THEME.primaryHover,
                                    cursor: pageNumber === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                                }}
                            >Next ›</button>
                        </div>

                        <div className="acp-pagesize-select" style={{ width: '110px' }}>
                            <Select
                                options={pageSizeSelectOptions}
                                value={pageSizeSelectOptions.find(o => o.value === pageSize)}
                                onChange={handlePageSizeChange}
                                isSearchable={false}
                                isDisabled={loading}
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                                classNamePrefix="react-select"
                            />
                        </div>
                    </div>
                )}
            </div>

            {viewMode === 'summary' && (
            <div className="card shadow-sm border-0 rounded-3 bg-white acp-card acp-trend-card">
                <div className="px-3 pt-3 pb-1 d-flex align-items-center justify-content-between acp-trend-header">
                    <div>
                        <div className="acp-trend-title">
                            Last {trendData.length || TREND_WEEKS_BACK} Week{(trendData.length || TREND_WEEKS_BACK) === 1 ? '' : 's'} Trend
                        </div>
                        <div className="acp-trend-subtitle">AI Module vs Manual check posting, by week</div>
                    </div>
                    {trendLoading && <RefreshCw size={14} style={{ animation: 'acp-spin 1s linear infinite', color: '#6c757d' }} />}
                </div>
                <div className="px-2 pb-3 acp-trend-chart-area" style={{ height: 320 }}>
                    {trendLoading ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                            Loading trend...
                        </div>
                    ) : trendData.length === 0 ? (
                        <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                            No data available.
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={trendData}
                                margin={isChartMobile ? { top: 8, right: 4, left: -20, bottom: 0 } : { top: 8, right: 16, left: 0, bottom: 0 }}
                                barGap={0}
                                barCategoryGap={isChartMobile ? '12%' : '20%'}
                            >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                                <XAxis
                                    dataKey={isChartMobile ? 'shortLabel' : 'label'}
                                    tick={{ fontSize: isChartMobile ? 10.5 : 12, fill: '#6c757d' }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    tickLine={false}
                                    interval={0}
                                />
                                <YAxis
                                    allowDecimals={false}
                                    width={isChartMobile ? 28 : 36}
                                    tick={{ fontSize: isChartMobile ? 10.5 : 12, fill: '#6c757d' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    labelFormatter={(value, payload) => (payload && payload[0] ? payload[0].payload.label : value)}
                                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                                    labelStyle={{ fontWeight: 600, color: '#33403c' }}
                                />
                                <Legend
                                    wrapperStyle={{ fontSize: isChartMobile ? 11 : 12 }}
                                    iconType="circle"
                                    iconSize={isChartMobile ? 8 : 10}
                                />
                                <Bar dataKey="ai" name="AI Module" fill={chartPrimary} radius={[4, 4, 0, 0]} maxBarSize={isChartMobile ? 22 : 40} />
                                <Bar dataKey="manual" name="Manual" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={isChartMobile ? 22 : 40} />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

export default AICheckPostingReport;